import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import type { ApprovalStage, PortalRole, PortalUser, StageApprovers } from '@jfh/intake-shared';
import { APPROVAL_STAGE_LABEL, canManageUsers } from '@jfh/intake-shared';
import { api, type Me } from '../lib/api.js';
import { ChoiceList, ConfirmDialog, Switch } from '../components/Choices.js';
import { PageHeader, Avatar, EmptyState } from '../components/Primitives.js';
import { Modal } from '../components/Modal.js';
import { useToast } from '../components/Toast.js';
import { Icon } from '../components/Icon.js';

const ROLE_LABEL: Record<PortalRole, string> = {
  root: 'Root admin',
  admin: 'Admin',
  staff: 'Staff',
};

/** What each role can do, in a line (F10). */
const ROLE_HINT: Record<PortalRole, string> = {
  staff: 'Works on applications and forms',
  admin: 'Also manages people, sharing and the audit log',
  root: 'Full access. Can’t be changed here.',
};

function RoleBadge({ role }: { role: PortalRole }) {
  const cls = role === 'staff' ? 'neutral' : role === 'admin' ? 'new' : 'approved';
  return <span className={`badge ${cls}`}>{ROLE_LABEL[role]}</span>;
}

export function Users() {
  const toast = useToast();
  const [me, setMe] = useState<Me | null>(null);
  const [users, setUsers] = useState<PortalUser[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [editing, setEditing] = useState<PortalUser | 'new' | null>(null);
  const [removing, setRemoving] = useState<PortalUser | null>(null);
  const [approvers, setApprovers] = useState<StageApprovers | null>(null);

  const refresh = () =>
    api
      .listUsers()
      .then((r) => setUsers(r.users))
      .catch((e: Error) => setError(e.message));

  useEffect(() => {
    api.me().then(setMe).catch(() => setMe(null));
    api
      .getApprovers()
      .then((r) => setApprovers(r.approvers))
      .catch(() => setApprovers(null));
    refresh();
  }, []);

  if (error) {
    return (
      <div style={{ padding: 28 }}>
        <EmptyState icon="shield" title="Couldn’t load the people" body={error} />
      </div>
    );
  }

  const allowed = me ? canManageUsers(me.role) : true;
  if (me && !allowed) {
    return (
      <div style={{ padding: 28 }}>
        <EmptyState
          icon="shield"
          title="Admins only"
          body="Only admins can manage who signs in."
        />
      </div>
    );
  }

  /** The stages this person is named for in Settings → Teams & approvals. */
  const approves = (u: PortalUser): string[] =>
    approvers
      ? (Object.keys(APPROVAL_STAGE_LABEL) as ApprovalStage[])
          .filter((st) => approvers[st]?.includes(u.id))
          .map((st) => APPROVAL_STAGE_LABEL[st])
      : [];

  // Throws to keep the confirm open with the message.
  const remove = async (u: PortalUser) => {
    await api.deleteUser(u.id);
    setRemoving(null);
    toast({ title: `${u.name || u.email} removed`, body: 'They can’t sign in any more.', icon: 'trash' });
    refresh();
  };

  return (
    <div style={{ padding: 28, maxWidth: 920 }} className="fade-in">
      <PageHeader
        title="Users"
        subtitle="Who can sign in to JFH Forms, and what they can do."
        actions={
          <button className="btn primary" onClick={() => setEditing('new')}>
            <Icon name="plus" size={15} /> Add a person
          </button>
        }
      />

      <div className="card" style={{ marginTop: 16, overflow: 'hidden' }}>
        {!users ? (
          <div style={{ padding: 28, color: 'var(--text-3)' }}>Loading…</div>
        ) : users.length === 0 ? (
          <EmptyState icon="inbox" title="No users yet" />
        ) : (
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13.5 }}>
            <thead>
              <tr style={{ textAlign: 'left', color: 'var(--text-3)' }}>
                <th style={th}>Person</th>
                <th style={th}>Can do</th>
                <th style={th}>Approves</th>
                <th style={th}>Sign-in</th>
                <th style={{ ...th, textAlign: 'right' }} aria-label="Actions" />
              </tr>
            </thead>
            <tbody>
              {users.map((u) => {
                const isSelf = me?.email.toLowerCase() === u.email.toLowerCase();
                const locked = u.role === 'root';
                return (
                  <tr key={u.id} style={{ borderTop: '1px solid var(--border)' }}>
                    <td style={td}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 11 }}>
                        <Avatar name={u.name || u.email} size={32} />
                        <div style={{ minWidth: 0 }}>
                          <div style={{ fontWeight: 700 }}>
                            {u.name || '—'}{' '}
                            {isSelf && (
                              <span style={{ color: 'var(--text-3)', fontWeight: 500 }}>(you)</span>
                            )}
                          </div>
                          <div className="mono" style={{ fontSize: 12, color: 'var(--text-2)' }}>
                            {u.email}
                          </div>
                        </div>
                      </div>
                    </td>
                    <td style={td}>
                      <RoleBadge role={u.role} />
                      <div style={{ fontSize: 12, color: 'var(--text-3)', marginTop: 3 }}>{ROLE_HINT[u.role]}</div>
                    </td>
                    <td style={{ ...td, fontSize: 13, color: approves(u).length ? 'var(--text-1)' : 'var(--text-3)' }}>
                      {approves(u).join(', ') || '—'}
                    </td>
                    <td style={td}>
                      <span className={`badge ${u.status === 'active' ? 'approved' : 'declined'}`}>
                        {u.status === 'active' ? 'Can sign in' : 'Blocked'}
                      </span>
                    </td>
                    <td style={{ ...td, textAlign: 'right' }}>
                      {locked ? (
                        <span style={{ color: 'var(--text-3)', fontSize: 12.5 }}>Protected</span>
                      ) : (
                        <div style={{ display: 'inline-flex', gap: 6 }}>
                          <button className="btn sm ghost" onClick={() => setEditing(u)}>
                            Edit
                          </button>
                          <button
                            className="btn sm ghost"
                            disabled={isSelf}
                            title={isSelf ? 'You cannot remove your own access' : undefined}
                            onClick={() => setRemoving(u)}
                            style={{ color: isSelf ? undefined : 'var(--st-declined)' }}
                          >
                            Remove
                          </button>
                        </div>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>

      <div style={{ marginTop: 12, fontSize: 12.5, color: 'var(--text-2)' }}>
        Who approves each stage is set in{' '}
        <Link to="/settings?section=teams" style={{ fontWeight: 700, color: 'var(--accent-text)' }}>
          Settings → Teams &amp; approvals
        </Link>
        .
      </div>

      <ConfirmDialog
        open={removing !== null}
        title={removing ? `Remove ${removing.name || removing.email}?` : ''}
        body="They lose access to JFH Forms straight away. Their past decisions stay in the audit log."
        confirmLabel="Remove"
        danger
        onCancel={() => setRemoving(null)}
        onConfirm={() => (removing ? remove(removing) : undefined)}
      />

      {editing && (
        <UserModal
          user={editing === 'new' ? null : editing}
          onClose={() => setEditing(null)}
          onSaved={(msg) => {
            setEditing(null);
            toast({ title: msg, icon: 'check' });
            refresh();
          }}
        />
      )}
    </div>
  );
}

const th: React.CSSProperties = { padding: '11px 16px', fontWeight: 600, fontSize: 12 };
const td: React.CSSProperties = { padding: '10px 16px', verticalAlign: 'middle' };

function UserModal({
  user,
  onClose,
  onSaved,
}: {
  user: PortalUser | null;
  onClose: () => void;
  onSaved: (msg: string) => void;
}) {
  const isNew = user === null;
  const [email, setEmail] = useState(user?.email ?? '');
  const [name, setName] = useState(user?.name ?? '');
  const [role, setRole] = useState<Exclude<PortalRole, 'root'>>(
    user && user.role !== 'root' ? user.role : 'staff',
  );
  const [status, setStatus] = useState(user?.status ?? 'active');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  const save = async () => {
    setBusy(true);
    setErr(null);
    try {
      if (isNew) {
        await api.createUser({ email, name, role });
        onSaved(`${name.trim() || email.trim()} added`);
      } else {
        await api.updateUser(user.id, { name, role, status });
        onSaved('Changes saved');
      }
    } catch (e) {
      setErr((e as Error).message);
      setBusy(false);
    }
  };

  return (
    <Modal open onClose={onClose}>
      <div style={{ padding: 22 }}>
        <h2 style={{ margin: '0 0 16px', fontSize: 17, fontWeight: 800 }}>
          {isNew ? 'Add a person' : `Edit ${user.name || user.email}`}
        </h2>

        <label className="field-label">Name</label>
        <input className="input" value={name} placeholder="Full name" onChange={(e) => setName(e.target.value)} />

        <label className="field-label" style={{ marginTop: 14 }}>Work email</label>
        <input
          className="input"
          type="email"
          value={email}
          disabled={!isNew}
          placeholder="name@jfhhorticultural.com"
          onChange={(e) => setEmail(e.target.value)}
        />
        {!isNew && (
          <div className="field-help">To change an email, remove the person and add them again.</div>
        )}

        <ChoiceList
          label="What can they do?"
          value={role}
          onChange={setRole}
          options={[
            { value: 'staff', label: 'Staff', description: 'Works on applications and builds forms.' },
            { value: 'admin', label: 'Admin', description: 'Everything staff can do, plus people, sharing and the audit log.' },
          ]}
        />

        {!isNew && (
          <div style={{ marginTop: 16 }}>
            <Switch
              on={status === 'active'}
              label={status === 'active' ? 'Can sign in' : 'Blocked'}
              help="Turn off to block sign-in without removing them."
              onChange={(on) => setStatus(on ? 'active' : 'disabled')}
            />
          </div>
        )}

        {err && (
          <div style={{ marginTop: 14, color: 'var(--st-declined)', fontSize: 13 }}>{err}</div>
        )}

        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 9, marginTop: 22 }}>
          <button className="btn ghost" onClick={onClose} disabled={busy}>
            Cancel
          </button>
          <button
            className="btn primary"
            onClick={save}
            disabled={busy || (isNew && !email.trim())}
          >
            {busy ? 'Saving…' : isNew ? 'Add' : 'Save'}
          </button>
        </div>
      </div>
    </Modal>
  );
}
